import { Fragment } from "react";
import { cn } from "@/utils/cn";
import { Icon } from "@/components/Icon/Icon";
import { Button, Pill } from "@/components/primitives";
import { usePopover } from "@/hooks/usePopover";
import type { Phase } from "@/utils/types/ui";
import type { Constraint, Session } from "@/utils/types/flavor";
import { TitleTag } from "./TitleTag";
import { PhaseSegmented } from "./PhaseSegmented";
import { FlavorDetailPopover } from "./FlavorDetailPopover";

type WorkbenchTopBarProps = {
  session: Session;
  starred: boolean;
  onToggleStar: () => void;
  phase: Phase;
  onPhaseChange: (phase: Phase) => void;
  onChangeTarget: () => void;
};

export function WorkbenchTopBar({
  session,
  starred,
  onToggleStar,
  phase,
  onPhaseChange,
  onChangeTarget,
}: WorkbenchTopBarProps) {
  const target = session.target;
  const constraints: Constraint[] = (target && target.constraints) || [];
  const baseRows = constraints.filter((r) => r.key === "base");
  const baseRow = baseRows[0];
  const appRow = constraints.find((r) => r.key === "app");

  const {
    open,
    anchorRect,
    anchorRef,
    show,
    scheduleHide,
    cancelHide,
    togglePin,
    close,
  } = usePopover();

  return (
    <div className="wb-topbar">
      <div className="hstack gap-2" style={{ flex: 1, minWidth: 0 }}>
        <button
          type="button"
          onClick={onToggleStar}
          title={starred ? "Starred — click to unstar" : "Star this flavor"}
          className={cn("wb-star-btn", starred && "active")}
        >
          <Icon name={starred ? "star-fill" : "star"} size={16} />
        </button>

        <span className="wb-title">{session.label || "Untitled flavor"}</span>

        {target && <Pill family={target.family} />}

        {target && (
          <Fragment>
            {baseRow && (
              <TitleTag
                icon="flask"
                label={baseRow.value}
                family={baseRow.family}
                more={Math.max(0, baseRows.length - 1)}
                onClick={togglePin}
              />
            )}
            {appRow && (
              <TitleTag icon="beaker" label={appRow.value} onClick={togglePin} />
            )}
          </Fragment>
        )}

        {target && (
          <button
            type="button"
            ref={anchorRef}
            className={cn("wb-info-btn", open && "active")}
            onClick={togglePin}
            onMouseEnter={show}
            onMouseLeave={scheduleHide}
            title="Show flavor details"
          >
            <Icon name="info" size={14} />
          </button>
        )}
      </div>

      <div className="hstack gap-2">
        <PhaseSegmented value={phase} onChange={onPhaseChange} />
        <div
          style={{
            width: 1,
            height: 18,
            background: "var(--line)",
            margin: "0 4px",
          }}
        />
        <Button size="sm">
          <Icon name="share" size={12} />
          Share
        </Button>
      </div>

      {open && (
        <FlavorDetailPopover
          target={target}
          anchorRect={anchorRect}
          anchorEl={anchorRef.current}
          onMouseEnter={cancelHide}
          onMouseLeave={scheduleHide}
          onChangeTarget={() => {
            close();
            onChangeTarget();
          }}
          onClose={close}
        />
      )}
    </div>
  );
}
